export type GeoTemplateId = 'universal' | 'aiOverviews' | 'aiMode' | 'gemini' | 'chatgpt' | 'perplexity' | 'copilot';
export type ArticleToneId = 'professional' | 'friendly' | 'expert' | 'practical';
export type ArticleAudienceId = 'general' | 'beginners' | 'professionals' | 'customers';
export type ArticleDepthId = 'short' | 'standard' | 'deep';

export interface WritingBrief {
    geoTemplate: GeoTemplateId;
    tone: ArticleToneId;
    audience: ArticleAudienceId;
    depth: ArticleDepthId;
    secondaryKeywords: string[];
}

export const defaultWritingBrief: WritingBrief = {
    geoTemplate: 'universal',
    tone: 'professional',
    audience: 'general',
    depth: 'standard',
    secondaryKeywords: [],
};

/** Picker cards. Legacy `aiMode` is not listed; it maps onto `aiOverviews`. */
export const GEO_TEMPLATE_IDS: GeoTemplateId[] = [
    'universal',
    'aiOverviews',
    'gemini',
    'chatgpt',
    'perplexity',
    'copilot',
];

/** Map legacy `aiMode` onto the combined Google search-AI template. */
export function normalizeGeoTemplate(id?: string | null): GeoTemplateId {
    if (id === 'aiMode') {
        return 'aiOverviews';
    }
    if (id && GEO_TEMPLATE_IDS.includes(id as GeoTemplateId)) {
        return id as GeoTemplateId;
    }
    return 'universal';
}

export const TONE_IDS: ArticleToneId[] = ['professional', 'friendly', 'expert', 'practical'];
export const AUDIENCE_IDS: ArticleAudienceId[] = ['general', 'beginners', 'professionals', 'customers'];
export const DEPTH_IDS: ArticleDepthId[] = ['short', 'standard', 'deep'];

export const GEO_TEMPLATE_ICONS: Record<GeoTemplateId, string> = {
    universal: 'public',
    aiOverviews: 'travel_explore',
    aiMode: 'travel_explore',
    gemini: 'flare',
    chatgpt: 'chat',
    perplexity: 'auto_awesome',
    copilot: 'assistant',
};

export type WritingPromptScope = 'article' | 'section' | 'meta' | 'h1' | 'h2' | 'h3' | 'question';

export interface GeoTemplateShape {
    name: string;
    productNames: string[];
    article: string;
    h1Heading: string;
    h1Body: string;
    h2Heading: string;
    h2Body: string;
    faq: string;
    meta: string;
    defaultH2Intents: string[];
}

const AI_OVERVIEWS_SHAPE: GeoTemplateShape = {
    name: 'AI Overviews',
    productNames: ['AI Overviews', 'AI Mode'],
    article:
        'Article shape for Google AI Overviews and AI Mode (they share one card). First sentences MUST answer the query. Then H2s as sub-questions covering how to choose (výběr), comparison (srovnání), who it is for (pro koho), and common mistakes. Short citable passages, lists, and FAQ. One article — not a mini-article under every H2.',
    h1Heading: 'Headline that matches the search query and includes the main keyword.',
    h1Body:
        'The first 40–60 words MUST answer the query directly so Google can cite them. Then one short definition. Do not preview later H2s or FAQ here.',
    h2Heading:
        'Write this H2 as a natural sub-question. Prefer how to choose (výběr), comparison (srovnání), or who it is for (pro koho) when the keyword allows.',
    h2Body:
        'Short citable passage. First sentence answers this H2. Prefer a list of 3–5 items or comparison criteria. Standalone. No FAQ, no intro recap.',
    faq: 'FAQ for follow-up queries AI Mode asks. Answer in the first sentence. 40–80 words, citable. Not a copy of an H2.',
    meta: 'Meta title 50–60 characters and description 150–160 characters that state the direct answer and include the keyword.',
    defaultH2Intents: ['how to choose', 'comparison', 'who it is for', 'common mistakes'],
};

export const GEO_TEMPLATE_SHAPES: Record<GeoTemplateId, GeoTemplateShape> = {
    universal: {
        name: 'Univerzální SEO',
        productNames: [],
        article:
            'Article shape for Univerzální SEO: direct answer first under H1, then clear practical H2s, then FAQ at the end. Classic SEO article that search engines and AI can both cite. Named entities, numbers, E-E-A-T cues.',
        h1Heading: 'Catchy SEO headline with the main keyword. Not a question unless the query already is one.',
        h1Body:
            'Direct answer in the first 40–60 words, then a short definition and what the article covers. No FAQ here.',
        h2Heading: 'Clear practical H2. Use a question only when the keyword is already a question.',
        h2Body: 'Self-contained 80–120 word section. Lists are fine. New facts only. No intro recap, no FAQ.',
        faq: 'FAQ belongs only at the end of the article. Answer in the first sentence. 40–80 words.',
        meta: 'Meta title 50–60 characters and description 150–160 characters with the main keyword.',
        defaultH2Intents: ['what it is', 'how it works', 'practical tips', 'common mistakes'],
    },
    aiOverviews: AI_OVERVIEWS_SHAPE,
    aiMode: AI_OVERVIEWS_SHAPE,
    gemini: {
        name: 'Gemini',
        productNames: ['Gemini'],
        article:
            'Article shape for Gemini (its own chat, not Google AI Overviews). Structured chat-like: tables, lists, and standalone passages Gemini can cite. Each block has clear criteria, numbers, and a verifiable fact.',
        h1Heading: 'Clear title Gemini can use as the chat topic. Include the main keyword.',
        h1Body: '40–60 word direct answer, then a compact list of what the article covers. No FAQ here.',
        h2Heading: 'Question or intent heading that can be a Gemini chat section title.',
        h2Body:
            'Standalone passage Gemini can lift. Include an HTML list or a simple <table> with criteria. One verifiable fact, number, or named source in this block. No “as above”.',
        faq: 'Short standalone FAQ Gemini can quote. Include one number or fact. Answer first.',
        meta: 'Meta title 50–60 characters and description 150–160 characters with a fact plus the keyword.',
        defaultH2Intents: ['key criteria', 'comparison', 'how to do it', 'numbers and facts'],
    },
    chatgpt: {
        name: 'ChatGPT',
        productNames: ['ChatGPT'],
        article:
            'Article shape for ChatGPT: conversational citable text. Answer first, then steps, then FAQ ready to insert into a chat reply. One coherent article.',
        h1Heading: 'Natural conversational headline with the main keyword.',
        h1Body: 'Answer in the first paragraph as if starting a chat reply, then say which steps follow. No FAQ here.',
        h2Heading: 'Conversational heading a chat could reuse. Prefer how-to / steps when the keyword allows.',
        h2Body: 'Answer first, then numbered steps if the heading is procedural. Conversational, quotable, no fluff. No FAQ.',
        faq: 'FAQ answers ready to paste into a ChatGPT reply. Direct, 40–80 words. Answer first.',
        meta: 'Meta title 50–60 characters and description 150–160 characters that sound like a useful chat answer.',
        defaultH2Intents: ['how to do it step by step', 'what to watch for', 'practical example'],
    },
    perplexity: {
        name: 'Perplexity',
        productNames: ['Perplexity'],
        article:
            'Article shape for Perplexity: facts, numbers, and short paragraphs with one thesis each. Ready to cite in search AI. Freshness (current year, typical ranges).',
        h1Heading: 'Factual title with the keyword. Avoid marketing language.',
        h1Body: 'Lead with facts, dates, and numbers. First paragraph = one thesis that answers the query. Short.',
        h2Heading: 'Heading that states the claim (criteria, range, comparison) — not a blog teaser.',
        h2Body:
            'One thesis per paragraph. Include a number, date, or range. Short scannable passage a retriever can lift. No “as above”.',
        faq: 'One-claim FAQ with a number or date. Answer in the first sentence.',
        meta: 'Meta title 50–60 characters and description 150–160 characters that lead with a fact and the keyword.',
        defaultH2Intents: ['key facts and numbers', 'comparison criteria', 'current ranges'],
    },
    copilot: {
        name: 'Copilot',
        productNames: ['Copilot'],
        article:
            'Article shape for Microsoft Copilot: direct answer immediately, question H2s, standalone passages with no “as above”. Facts, dates, and verifiable sources Copilot can lift into a recommendation.',
        h1Heading: 'Headline matching a Bing/Copilot query with the main keyword.',
        h1Body:
            'Direct answer in the first 40–60 words with zero warmup. Then a short definition. No later-section spoilers.',
        h2Heading: 'Natural question or intent (how, what, who, vs).',
        h2Body:
            'First sentence stands alone with zero prior context — no “as above” or dangling “this”. 60–100 words. Include one verifiable fact, date, or third-party source.',
        faq: 'FAQ matching Copilot follow-up queries. Answer first; include a fact or date.',
        meta: 'Meta title 50–60 characters and description 150–160 characters with a fact Copilot can quote.',
        defaultH2Intents: ['how does it work', 'who is it for', 'how to choose', 'comparison'],
    },
};

export const GEO_TEMPLATE_PROMPTS: Record<GeoTemplateId, string> = {
    universal: GEO_TEMPLATE_SHAPES.universal.article,
    aiOverviews: GEO_TEMPLATE_SHAPES.aiOverviews.article,
    aiMode: GEO_TEMPLATE_SHAPES.aiMode.article,
    gemini: GEO_TEMPLATE_SHAPES.gemini.article,
    chatgpt: GEO_TEMPLATE_SHAPES.chatgpt.article,
    perplexity: GEO_TEMPLATE_SHAPES.perplexity.article,
    copilot: GEO_TEMPLATE_SHAPES.copilot.article,
};

export function geoTemplateShape(id?: string | null): GeoTemplateShape {
    return GEO_TEMPLATE_SHAPES[normalizeGeoTemplate(id)];
}

export const TONE_PROMPTS: Record<ArticleToneId, string> = {
    professional: 'Tone: professional yet accessible. Clear, confident, no jargon unless explained.',
    friendly: 'Tone: warm and conversational, still precise. Write as a helpful expert talking to a reader.',
    expert: 'Tone: authoritative and specific. Use precise terms, criteria, and reasoned recommendations.',
    practical: 'Tone: action-first. Prefer how-to steps, checklists, and concrete tips over theory.',
};

export const AUDIENCE_PROMPTS: Record<ArticleAudienceId, string> = {
    general: 'Audience: broad public and potential customers. Explain terms the first time they appear.',
    beginners: 'Audience: beginners. Define basics early, avoid assuming prior knowledge, use simple examples.',
    professionals: 'Audience: practitioners. Skip obvious basics, go deeper on trade-offs and criteria.',
    customers: 'Audience: people comparing options before a purchase. Emphasize fit, costs, and decision criteria.',
};

export const DEPTH_PROMPTS: Record<ArticleDepthId, string> = {
    short: 'Length: concise (~800 words, range 600–900). Fewer H2s, denser facts, no padding.',
    standard: 'Length: standard article (~1200 words, range 1100–1400) with a short FAQ.',
    deep: 'Length: in-depth (~2000 words, range 1800–2200). More H2/H3 sections, comparisons, and worked examples.',
};


export function composeGenerationInstructions(brief: Partial<WritingBrief>): string {
    const shape = geoTemplateShape(brief.geoTemplate);
    const tone = (brief.tone && TONE_PROMPTS[brief.tone]) || TONE_PROMPTS.professional;
    const audience = (brief.audience && AUDIENCE_PROMPTS[brief.audience]) || AUDIENCE_PROMPTS.general;
    const depth = (brief.depth && DEPTH_PROMPTS[brief.depth]) || DEPTH_PROMPTS.standard;
    const extras = (brief.secondaryKeywords || []).map(k => k.trim()).filter(Boolean);
    const lines = [
        'Writing brief for this article:',
        `GEO template: ${shape.name}. Follow the section rules below — do not paste this whole card into every heading.`,
        shape.article,
        `H1: ${shape.h1Heading} ${shape.h1Body}`,
        `H2/H3: ${shape.h2Heading} ${shape.h2Body}`,
        `FAQ: ${shape.faq}`,
        `Meta: ${shape.meta}`,
        tone,
        audience,
        depth,
        extras.length ? `Secondary keywords to weave in naturally: ${extras.join(', ')}.` : '',
        'AI systems cite short passages, not whole pages. Prefer quotable facts, named entities, and comparison criteria. Do not mention internal product or data-provider names.',
        'Write ONE article. Do not repeat the intro under every H2. Do not embed FAQ inside H2.',
        'Headings (H1–H3 and FAQ questions): NEVER use the raw keyword as the entire heading. Write a natural heading in the article language that a human would click. Cover the topic of the keyword with inflection, word order, and optional extra words.',
        'Bad heading: "BARF pro psy Brno", "pos", "kredit t mobile". Good heading: "Kde koupit BARF v Brně a na co si dát pozor", "Jak souvisí POS s platebním terminálem v restauraci".',
        'Body: do not force the exact keyword token sequence. Use natural inflected forms, pronouns, and paraphrases. Exact-match stuffing is a quality failure.',
        'Živé odpovědi AI chatů jsou výzkum, ne citace k vložení. Napiš jeden souvislý článek, který ta data použije — ne kopii chatu a ne dump přepisu.',
        'Použij informace z vybraných AI odpovědí tam, kam se v článku logicky hodí, nebo kde text obohatí či doplní pro čtenáře. Přepiš je do přirozených vět, neslepuj chaty do úvodu a nedávej do odstavců markdown seznamy (− SEO). Konkrétní čísla, procenta, ceny, roky a názvy studií uveď v kapitole, které se týkají.',
        'Věty musí být dokončené. Žádné useknuté slovo (ve vyh., billb., jiných akt.), žádné rozbité mezery (soubor a ktivit), žádné backtick-citace.',
        'Při přebírání oprav mezery a češtinu: vtomto → v tomto; a ktivit → aktivit.',
        'H2 piš jako přirozené nadpisy, které by člověk kliknul — ne surové klíčové slovo. Zaškrtnutá související klíčová slova jsou témata H2, která musíš pokrýt.',
        'Přesně jeden H1 nadpis a jeden úvodní odstavec. Nikdy druhý H1.',
        'Neopakuj kapitoly ani FAQ. Do textu nedávej # ani ---.',
    ];
    return lines.filter(Boolean).join('\n');
}

export function composeSectionInstructions(
    brief: Partial<WritingBrief>,
    part: WritingPromptScope = 'h2'
): string {
    const shape = geoTemplateShape(brief.geoTemplate);
    const tone = (brief.tone && TONE_PROMPTS[brief.tone]) || TONE_PROMPTS.professional;
    const audience = (brief.audience && AUDIENCE_PROMPTS[brief.audience]) || AUDIENCE_PROMPTS.general;
    const extras = (brief.secondaryKeywords || []).map(k => k.trim()).filter(Boolean);
    const isFaq = part === 'question';
    const isH1 = part === 'h1';
    const label = isFaq ? 'FAQ' : isH1 ? 'H1' : part === 'meta' ? 'meta' : 'H2';
    const rules = isFaq
        ? shape.faq
        : isH1
            ? `${shape.h1Heading} ${shape.h1Body}`
            : part === 'meta'
                ? shape.meta
                : `${shape.h2Heading} ${shape.h2Body}`;
    return [
        `Writing brief for this one ${label} (not a full article):`,
        `GEO template: ${shape.name}. Apply only the ${label} rules — do not dump the whole template card into this chunk.`,
        rules,
        tone,
        audience,
        extras.length ? `Secondary keywords to weave in naturally if they fit this section: ${extras.join(', ')}.` : '',
        'NEVER use the raw keyword as the entire heading. Write a natural heading in the article language. Do not force the exact keyword token sequence in the body.',
        'Použij informace z vybraných AI odpovědí tam, kam se v této sekci logicky hodí, nebo kde text obohatí či doplní pro čtenáře. Přepiš je do přirozených vět. Věty dokončené, žádné ve vyh. / billb. / a ktivit / backtick-citace.',
        isFaq
            ? 'Write 40–80 words. Answer in the first sentence. This is not a full article.'
            : isH1
                ? 'About 80–110 words. This is the H1 only — not a full article, FAQ, or later H2s.'
                : 'Write 80–120 words. No FAQ, no intro recap, no markdown headings, no nested H2.',
    ].filter(Boolean).join('\n');
}

export function composeUserPrompt(userBrief: string, brief: Partial<WritingBrief>): string {
    const text = (userBrief || '').trim();
    const instructions = composeGenerationInstructions(brief);
    return text ? `${text}\n\n${instructions}` : instructions;
}
